import { useState } from "react";
import { formatRelativeTime } from "utils/helper";
import { waterEventStateInfo } from "utils/waterEvents";
import PumpOperationsPanel from "components/experiment/PumpOperationsPanel";
import {
  DEFAULT_PUMP_CONFIG,
  formatPumpDuration,
  pumpCmError,
  pumpDurationSeconds,
} from "utils/pumpFormat";

// Enum-driven status card, mirroring AWDStatusCard's STATUS_CONFIG pattern -
// keyed by pump_status.status ("running"/"stopped"), not by reason: reason
// only decides the styling/text of the sub-line below the headline state.
const STATUS_CONFIG = {
  running: {
    label: "Running",
    bg: "bg-blue-100",
    border: "border-blue-400",
    text: "text-blue-800",
    dot: "bg-blue-500",
  },
  stopped: {
    label: "Stopped",
    bg: "bg-slate-100",
    border: "border-slate-300",
    text: "text-slate-700",
    dot: "bg-slate-400",
  },
};

// A stop nobody asked for must look different from a normal one. Since the
// pump runs by distance (firmware 1.2.0) the normal ending is "completed"; the
// older reasons only appear in history from earlier firmware.
const REASON_INFO = {
  completed: { label: "Finished: pumped the requested distance", warn: false },
  operator_stop: { label: "Stopped by operator", warn: false },
  timeout: { label: "Stopped: safety timeout reached", warn: true },
  min_level_reached: { label: "Stopped: minimum level reached", warn: false },
  stale_level_data: { label: "Stopped: water-level data is stale", warn: true },
};

const COMMAND_VERBS = { pump_fill: "Fill", pump_drain: "Drain", pump_stop: "Stop" };

export default function PumpControlPanel({
  activeController,
  disabled,
  pumpConfig,
  onFill,
  onDrain,
  onStop,
  // The operations log folded at the bottom of the card (see PumpOperationsPanel); not shown without
  // `operations`. runWindow is the run on the chart ({runId, start, end}).
  operations = null,
  operationsError = null,
  runWindow = null,
}) {
  const config = pumpConfig || DEFAULT_PUMP_CONFIG;
  const [cmText, setCmText] = useState("1");
  const [submittingAction, setSubmittingAction] = useState(null); // "fill" | "drain" | "stop" | null
  const [message, setMessage] = useState("");

  const pumpStatus = activeController?.pump_status;
  const latestCommand = activeController?.latest_pump_command;

  // Same live-derivation convention as GreenhouseRunForm's ackLine (a real
  // bug fixed 2026-09-15): never freeze this at submit time, always derive
  // from the polled activeController on every render.
  const ackLine = (() => {
    if (!latestCommand) return null;
    const verb = COMMAND_VERBS[latestCommand.command] || latestCommand.command;
    if (!latestCommand.acknowledged) {
      return `${verb} command sent — waiting for the controller to acknowledge…`;
    }
    if (latestCommand.ack_status && latestCommand.ack_status !== "ok") {
      return `${verb} command rejected by the controller${
        latestCommand.ack_reason ? `: ${latestCommand.ack_reason}` : "."
      }`;
    }
    return `${verb} command acknowledged by the controller ✓`;
  })();

  const submit = async (action, fn) => {
    setMessage("");
    setSubmittingAction(action);
    try {
      await fn();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmittingAction(null);
    }
  };

  // The firmware owns the real rule; this mirrors the backend's validation so a
  // bad value is caught before a round trip.
  const cmError = pumpCmError(cmText, config);
  const cm = cmError ? null : Number(cmText);
  const unsupported = activeController && activeController.pump_supported === false;
  // An operation in flight (photo -> pump -> photo): the backend refuses a second one, so say what it
  // is doing and hold Fill/Drain until it is over. Stop stays available.
  const currentOperation = activeController?.current_water_event || null;

  const submitMove = (action, fn) => {
    if (cmError) {
      setMessage(cmError);
      return;
    }
    submit(action, () => fn(cm));
  };
  const submitFill = () => submitMove("fill", onFill);
  const submitDrain = () => submitMove("drain", onDrain);
  const submitStop = () => submit("stop", onStop);

  const busy = disabled || submittingAction !== null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Pump</p>

      {pumpStatus ? (
        (() => {
          const cfg = STATUS_CONFIG[pumpStatus.status] || STATUS_CONFIG.stopped;
          const reasonInfo = pumpStatus.reason ? REASON_INFO[pumpStatus.reason] : null;
          return (
            <div className={`mt-2 rounded-xl border-2 ${cfg.border} ${cfg.bg} p-3`}>
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${cfg.dot}`} aria-hidden="true" />
                <span className={`text-sm font-semibold ${cfg.text}`}>
                  {cfg.label}
                  {pumpStatus.direction ? ` — ${pumpStatus.direction}` : ""}
                </span>
              </div>
              {pumpStatus.cm != null && (
                <p className={`mt-1 text-xs ${cfg.text}`} data-testid="pump-run-size">
                  {pumpStatus.cm} cm
                  {pumpStatus.expected_seconds != null
                    ? ` (about ${formatPumpDuration(pumpStatus.expected_seconds)})`
                    : ""}
                </p>
              )}
              {pumpStatus.status === "running" && pumpStatus.elapsed_seconds != null && (
                <p className={`mt-1 text-xs ${cfg.text}`}>
                  Elapsed: {Math.round(pumpStatus.elapsed_seconds)}s
                </p>
              )}
              {reasonInfo && (
                <p
                  className={`mt-1 text-xs font-medium ${
                    reasonInfo.warn ? "text-amber-700" : "text-slate-600"
                  }`}
                >
                  {reasonInfo.label}
                </p>
              )}
              {pumpStatus.updated_at && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {formatRelativeTime(pumpStatus.updated_at)}
                </p>
              )}
            </div>
          );
        })()
      ) : (
        <p className="mt-2 text-xs text-slate-500">No pump status received yet.</p>
      )}

      {ackLine && (
        <p
          role="status"
          className={`mt-2 text-xs ${
            latestCommand?.acknowledged
              ? latestCommand.ack_status === "ok"
                ? "text-emerald-700"
                : "text-red-700"
              : "text-slate-500"
          }`}
        >
          {ackLine}
        </p>
      )}

      {unsupported && (
        <p role="alert" className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
          This controller runs firmware {activeController.last_reported_fw || "unknown"}; fill/drain by
          distance needs {config.min_firmware || "a newer version"}. Update it from Settings &gt; Config &gt;
          Firmware.
        </p>
      )}

      {currentOperation && (
        <p
          role="status"
          data-testid="pump-current-operation"
          className="mt-2 rounded-lg bg-blue-50 p-2 text-xs text-blue-800"
        >
          {currentOperation.direction === "drain" ? "Drain" : "Fill"} {currentOperation.cm} cm:{" "}
          {waterEventStateInfo(currentOperation.state).label}
          {currentOperation.source === "manual" ? " (by hand)" : " (scheduled)"}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-sm font-semibold text-slate-700">
          Distance (cm)
          <input
            type="number"
            min={config.min_cm_per_op}
            max={config.max_cm_per_op}
            step="0.1"
            value={cmText}
            onChange={(event) => setCmText(event.target.value)}
            aria-label="Distance (cm)"
            className="mt-1 block w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          onClick={submitFill}
          disabled={busy || unsupported || !!currentOperation}
          className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
        >
          {submittingAction === "fill" ? "Filling…" : "Fill"}
        </button>
        <button
          onClick={submitDrain}
          disabled={busy || unsupported || !!currentOperation}
          className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 disabled:opacity-50"
        >
          {submittingAction === "drain" ? "Draining…" : "Drain"}
        </button>
        <button
          onClick={submitStop}
          disabled={busy}
          className="rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-bold text-red-700 disabled:opacity-50"
        >
          {submittingAction === "stop" ? "Stopping…" : "Stop"}
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500" data-testid="pump-duration-preview">
        {cmError
          ? cmError
          : `${cm} cm takes about ${formatPumpDuration(pumpDurationSeconds(cm, config))} (${config.seconds_per_cm} s per cm, fill or drain).`}
      </p>

      {message && <p role="status" className="mt-2 text-sm text-slate-700">{message}</p>}

      {operations && (
        <PumpOperationsPanel
          events={operations}
          error={operationsError}
          runId={runWindow?.runId ?? null}
          start={runWindow?.start ?? null}
          end={runWindow?.end ?? null}
        />
      )}
    </div>
  );
}
